import { AccountsService } from 'base/accounts_service';
import { AnalyticsService } from 'base/analytics';
import { UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { MAX_CONCEPTS } from 'base/styleguide/model';
import { Button } from 'base/ui/button';
import {
  IconChevronDown,
  IconChevronLeft,
  IconEdit,
  IconFeedback,
  IconLinked,
  IconLogo,
  IconPitch,
  IconRedoSmall,
  IconReplaceAi,
  IconUndoSmall,
} from 'base/ui/icons';
import { Inline } from 'base/ui/layout';
import { MenuButton } from 'base/ui/menu';
import { menuDivider, menuItem, MenuModel, menuText } from 'base/ui/menu_model';
import { ToastQueue } from 'base/ui/toast';
import { TextSmall } from 'base/ui/typography';
import { AccessLevel, BlocksService } from 'blocks/blocks_service';
import { ProjectEditingActions } from 'blocks/editor/editing_actions/project';
import { SelectionStore } from 'blocks/editor/selection/store';
import { SelectionActions } from 'blocks/editor/selection_actions';
import { ProjectSession } from 'blocks/editor/session/session';
import { BrandPanel } from 'blocks/editor/styleguide/brand_panel';
import { ColorsEditorPanel } from 'blocks/editor/styleguide/colors_editor';
import { ConceptPreviews } from 'blocks/editor/styleguide/concept_previews';
import { getGlobalConceptsStore } from 'blocks/editor/styleguide/concept_store';
import { getExtraProjectShareData } from 'blocks/editor/styleguide/helpers';
import styles from 'blocks/editor/styleguide/pages.css';
import {
  SidebarNavigation,
  styleguideConceptId,
  styleguideView,
} from 'blocks/editor/styleguide/routes';
import {
  globalStyleGuideViewStore,
  StyleGuide,
} from 'blocks/editor/styleguide/styleguide';
import { TypographyEditorPanel } from 'blocks/editor/styleguide/typography_panel';
import { ManageTeamModel } from 'blocks/manage_account/manage_team_model';
import classNames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { getGlobalDesignStore } from '../design/design_store';
import { UseProjectOwner } from '../editor';
import { unrefSection } from '../model_helpers';
import { SidebarNavigation as WireframeNavigation } from '../sidebar/routes';
import { CardStylesPanel } from './card_styles_panel';
import { DemoPage } from './demo_page';
import { CopyStyleGuide } from './dev_tools';
import { FeedbackPanel } from './feedback_panel';
import { ImageryPanel } from './imagery_panel';
import { TypographyOnboardingPanel } from './typography_onboarding_panel';
import { UiStylesPanel } from './ui_styles_panel';

export const StyleguidePages = observer(function _StyleguidePages({
  accountsService,
  selection,
  selectionActions,
  analytics,
  session,
  editing,
  library,
  toastQueue,
  blocksService,
  wireframeNavigation,
  projectOwner,
}: {
  accountsService: AccountsService;
  session: ProjectSession;
  toastQueue: ToastQueue;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  editing: ProjectEditingActions | undefined;
  library: LibraryCache;
  blocksService: BlocksService;
  wireframeNavigation: WireframeNavigation;
  projectOwner: UseProjectOwner;
}) {
  const [manageTeamModel, setManageTeamModel] = useState<ManageTeamModel>();

  useEffect(() => {
    void accountsService
      .getTeam()
      .then((res) =>
        setManageTeamModel(new ManageTeamModel(res, accountsService)),
      );
  }, [accountsService]);

  const currentView = styleguideView.get();

  const onlyShowConceptPreviews = session.accessLevel < AccessLevel.OWNER;

  const hasNonEmptySections = session.project.homePage.sections.some(
    (sref) => unrefSection(sref, session.project)?.element.current != null,
  );
  const showDemoPage = !onlyShowConceptPreviews && !hasNonEmptySections;

  const conceptsStore = getGlobalConceptsStore();

  useEffect(
    () =>
      reaction(
        () => styleguideConceptId.get(),
        (conceptId) => {
          if (conceptId != null) {
            conceptsStore.setActiveConceptByUuid(conceptId);
          }
        },
        { fireImmediately: true },
      ),
    [conceptsStore],
  );

  useEffect(
    () =>
      reaction(
        () => [conceptsStore.activeConcept.state.uuid, styleguideView.get()],
        ([uuid, view]) => {
          styleguideConceptId.set(view === 'builder' ? uuid : undefined);
        },
        { fireImmediately: false },
      ),
    [conceptsStore],
  );

  if (!showDemoPage && session.project.concepts.length === 0) {
    session.undo.doPermanently(() =>
      conceptsStore
        .createNewConcept({
          createdBy: session.createdBy,
        })
        .shuffle({
          typography: { heading: true, body: true },
          theme: true,
          colors: true,
          schemes: true,
          uiStyle: true,
          cardStyle: true,
          // include demo images for the project's first concept
          images: true,
        }),
    );
  }

  return (
    <div
      className={classNames(styles.container, styles.left)}
      onMouseUp={(e) => {
        // Only close the sidebar if the click is on the container itself (i.e.
        // the grey background), not any of its children.
        if (e.target === e.currentTarget) {
          globalNavigation.close();
        }
      }}
    >
      <div className={styles.page} onMouseUp={() => globalNavigation.close()}>
        {!showDemoPage && !onlyShowConceptPreviews && (
          <Header
            library={library}
            analytics={analytics}
            manageTeamModel={manageTeamModel}
            toastQueue={toastQueue}
            session={session}
            hideMenu={showDemoPage}
          />
        )}
        {showDemoPage ? (
          <DemoPage
            project={session.project}
            wireframeNavigation={wireframeNavigation}
            toastQueue={toastQueue}
          />
        ) : onlyShowConceptPreviews || currentView === 'preview' ? (
          <ConceptPreviews
            session={session}
            library={library}
            selection={selection}
            selectionActions={selectionActions}
            analytics={analytics}
            projectOwner={projectOwner}
            blocksService={blocksService}
            editing={!!editing}
            toastQueue={toastQueue}
          />
        ) : (
          <StyleGuide
            selection={selection}
            selectionActions={selectionActions}
            analytics={analytics}
            session={session}
            editing={editing}
            library={library}
            navigation={globalNavigation}
            toastQueue={toastQueue}
            blocksService={blocksService}
          />
        )}
      </div>

      {currentView === 'builder' &&
        !onlyShowConceptPreviews &&
        !globalStyleGuideViewStore.fullWidth &&
        !showDemoPage && (
          <Sidebar
            analytics={analytics}
            navigation={globalNavigation}
            toastQueue={toastQueue}
            blocksService={blocksService}
            session={session}
            library={library}
          />
        )}
    </div>
  );
});

const Header = observer(function _Header({
  library,
  analytics,
  manageTeamModel,
  toastQueue,
  session,
  hideMenu = false,
}: {
  library: LibraryCache;
  analytics: AnalyticsService;
  manageTeamModel?: ManageTeamModel;
  toastQueue: ToastQueue;
  session: ProjectSession;
  hideMenu?: boolean;
}) {
  const conceptsStore = getGlobalConceptsStore();
  const activeConcept = conceptsStore.activeConcept;
  const allConcepts = conceptsStore.concepts;

  const menu = [
    ...allConcepts.map((concept, index) =>
      menuItem({
        label: `Concept ${index + 1}`,
        onClick: () => {
          conceptsStore.setActiveConceptByUuid(concept.state.uuid);
          styleguideView.set('builder');
          analytics.track({
            event: 'project:view_concept_button_click',
            concept_number: index + 1,
          });
        },
        checked:
          styleguideView.get() === 'builder' && concept === activeConcept,
      }),
    ),
    menuText({
      label:
        'We recommend creating at least three distinct concepts before sharing',
      size: 'tiny',
    }),
    ...(styleguideView.get() === 'builder'
      ? // Only show these options in builder view
        [
          menuDivider(),
          menuItem({
            label: 'New Concept',
            onClick: () => {
              const newConcept = conceptsStore.createNewConcept({
                createdBy:
                  manageTeamModel?.members.find((member) => member.isMe)?.id ??
                  -1,
              });
              conceptsStore.setActiveConceptByUuid(newConcept.state.uuid);
              newConcept.shuffle({
                typography: { heading: true, body: true },
                theme: true,
                colors: true,
                schemes: true,
                uiStyle: true,
                cardStyle: true,
              });
              toastQueue.push({
                label: `Concept ${conceptsStore.concepts.length} created!`,
                theme: 'dark',
              });
              analytics.track({
                event: 'project:new_concept_button_click',
                concept_number: conceptsStore.concepts.length,
              });
            },
            disabled: conceptsStore.concepts.length === MAX_CONCEPTS,
            disabledReason: `Cannot create more than ${MAX_CONCEPTS} concepts`,
          }),
          menuItem({
            label: 'Duplicate Concept',
            onClick: () => {
              const currentActiveIndex = conceptsStore.concepts.findIndex(
                (concept) => concept === conceptsStore.activeConcept,
              );
              const {
                uuid: _,
                galleryPosition: __,
                ...currentConcept
              } = conceptsStore.activeConcept.state;
              const newConcept = conceptsStore.createNewConcept(currentConcept);
              conceptsStore.setActiveConceptByUuid(newConcept.state.uuid);
              const conceptNumber = currentActiveIndex + 1;
              toastQueue.push({
                label: `Concept ${conceptNumber} duplicated!`,
                theme: 'dark',
              });
              analytics.track({
                event: 'project:duplicate_concept_button_click',
                concept_number: conceptNumber,
              });
            },
            disabled: conceptsStore.concepts.length === MAX_CONCEPTS,
            disabledReason: `Cannot create more than ${MAX_CONCEPTS} concepts`,
          }),
          menuItem({
            label: 'Delete Concept',
            onClick: () => {
              const currentActiveIndex = conceptsStore.concepts.findIndex(
                (concept) => concept === conceptsStore.activeConcept,
              );
              conceptsStore.deleteConcept(
                conceptsStore.activeConcept.state.uuid,
              );
              const { uuid } =
                conceptsStore.concepts[currentActiveIndex - 1].state;
              conceptsStore.setActiveConceptByUuid(uuid);

              const conceptNumber = currentActiveIndex + 1;
              toastQueue.push({
                label: `Concept ${conceptNumber} deleted!`,
                theme: 'dark',
                action: {
                  label: 'Undo',
                  onClick: () => session.undo.undo(),
                },
              });
              analytics.track({
                event: 'project:delete_concept_button_click',
                concept_number: conceptNumber,
              });
            },
            disabled: conceptsStore.concepts.length === 1,
            disabledReason: 'Cannot delete the last concept',
          }),
        ]
      : []),
  ] satisfies MenuModel;

  return (
    <header className={styles.header}>
      {styleguideView.get() === 'builder' ? (
        <>
          <MenuButton
            label={`Concept ${allConcepts.indexOf(activeConcept) + 1}`}
            iconRight={IconChevronDown}
            variant="tertiary"
            search="hidden"
            menu={menu}
          />
          <Inline spacing={8} yalign="center">
            {session.flags.get('enableDesignView') && <DesignSyncControls />}
            <Button
              variant="secondary"
              label="Pitch Concepts"
              icon={IconPitch}
              onClick={() => {
                styleguideView.set('preview');
                analytics.track({
                  event: 'project:top_3_concepts_button_click',
                });
              }}
              tooltip="Prepare your concepts to share"
            />
            {session.flags.get('enableReactExportDebugging') && (
              <CopyStyleGuide
                concept={activeConcept.state}
                toastQueue={toastQueue}
              />
            )}
            <Button
              variant="primary"
              background="ai"
              tooltip="Surprise me"
              icon={IconReplaceAi}
              onClick={() => {
                activeConcept.shuffle({
                  typography: { heading: true, body: true },
                  colors: true,
                  theme: true,
                  showProgressToast: true,
                  showDoneToast: true,
                  schemes: true,
                  uiStyle: true,
                  cardStyle: true,
                });
                analytics.track({
                  event: 'project:surprise_me_button_click',
                });
              }}
            />
          </Inline>
        </>
      ) : (
        <>
          <div>
            {!hideMenu && (
              <Button
                icon={IconChevronLeft}
                variant="tertiary"
                label="Concept"
                onClick={() => styleguideView.set('builder')}
              />
            )}
          </div>
          <Button
            icon={IconLinked}
            variant="primary"
            label="Copy link"
            onClick={async () => {
              navigator.clipboard.writeText(window.location.href);
              toastQueue.push({
                label: 'Copied to clipboard',
                theme: 'dark',
              });
              session.analytics.track({
                event: 'project:copy_share_link_button_click',
                extra: await getExtraProjectShareData(
                  session.project.state,
                  library,
                ),
              });
            }}
          />
        </>
      )}
    </header>
  );
});

const Sidebar = observer(function _Sidebar({
  analytics,
  navigation,
  toastQueue,
  blocksService,
  session,
  library,
}: {
  analytics: AnalyticsService;
  navigation: SidebarNavigation;
  toastQueue: ToastQueue;
  blocksService: BlocksService;
  session: ProjectSession;
  library: LibraryCache;
}) {
  const onClickEditStyleguide = React.useCallback(() => {
    navigation.toggleToplevel('colors');
    analytics.track({
      event: 'project:edit_color_button_click',
    });
  }, [analytics, navigation]);
  const onClickEditBrand = React.useCallback(() => {
    navigation.toggleToplevel('brand');
    analytics.track({
      event: 'project:edit_color_button_click',
    });
  }, [analytics, navigation]);

  const { toplevel } = navigation;
  const { undo } = session;

  const conceptsStore = getGlobalConceptsStore();
  const activeConcept = conceptsStore.activeConcept;

  const showFontOnboarding =
    !activeConcept.state.fontOnboardingCompleted &&
    navigation.at?.path === 'fontFamily' &&
    navigation.at.target === 'heading';

  const getTopLevelPanel = React.useCallback(() => {
    if (!toplevel) return null;

    switch (toplevel) {
      case 'brand':
        return (
          <BrandPanel
            session={session}
            analytics={analytics}
            navigation={navigation}
            toastQueue={toastQueue}
          />
        );
      case 'colors':
        return (
          <ColorsEditorPanel
            analytics={analytics}
            navigation={navigation}
            toastQueue={toastQueue}
          />
        );
      case 'fontFamily':
        if (showFontOnboarding) {
          return (
            <TypographyOnboardingPanel
              navigation={navigation}
              blocksService={blocksService}
              analytics={analytics}
              toastQueue={toastQueue}
            />
          );
        }
        return (
          <TypographyEditorPanel
            analytics={analytics}
            session={session}
            navigation={navigation}
            blocksService={blocksService}
            toastQueue={toastQueue}
          />
        );
      case 'feedback':
        return (
          <FeedbackPanel
            navigation={navigation}
            toastQueue={toastQueue}
            blocksService={blocksService}
            session={session}
          />
        );
      case 'imagery':
        return (
          <ImageryPanel
            session={session}
            navigation={navigation}
            library={library}
            undo={undo}
            toastQueue={toastQueue}
            analytics={analytics}
            concept={activeConcept}
            section={
              navigation.at?.path === 'imagery' ? navigation.at.section : null
            }
          />
        );
      case 'uiStyles':
        return <UiStylesPanel navigation={navigation} library={library} />;
      case 'cardStyles':
        return <CardStylesPanel navigation={navigation} />;
      default:
        throw new UnreachableError(toplevel);
    }
  }, [
    toplevel,
    session,
    analytics,
    navigation,
    toastQueue,
    showFontOnboarding,
    blocksService,
    library,
    undo,
    activeConcept,
  ]);

  const panel = getTopLevelPanel();

  const feedbackButtonRef = React.useRef<HTMLElement>(null);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarTop}>
        {panel && (
          <div
            className={classNames(
              styles.panel,
              toplevel === 'feedback' && styles.bottomPanel,
              toplevel === 'fontFamily' &&
                showFontOnboarding &&
                styles.fullHeightPanel,
            )}
          >
            {panel}
          </div>
        )}
        <Button
          variant="secondary"
          icon={IconEdit}
          size="large"
          onClick={onClickEditStyleguide}
          checked={
            toplevel === 'colors' ||
            toplevel === 'fontFamily' ||
            toplevel === 'imagery'
          }
          tooltip="Edit"
          tooltipPlacement="horizontal"
        />
        <Button
          variant="secondary"
          icon={IconLogo}
          size="large"
          onClick={onClickEditBrand}
          checked={toplevel === 'brand'}
          tooltip="Brand"
          tooltipPlacement="horizontal"
        />
      </div>
      <div
        className={styles.sidebarSpacer}
        onClick={() => globalNavigation.close()}
      />
      <div className={styles.sidebarBottom}>
        <Button
          variant="secondary"
          background="transparent"
          icon={IconUndoSmall}
          size="large"
          onClick={() => undo.undo()}
          disabled={!undo.canUndo}
          tooltip="Undo"
          tooltipPlacement="horizontal"
          tooltipShortcut="⌘Z"
        />
        <Button
          variant="secondary"
          background="transparent"
          icon={IconRedoSmall}
          size="large"
          onClick={() => undo.redo()}
          disabled={!undo.canRedo}
          tooltip="Redo"
          tooltipPlacement="horizontal"
          tooltipShortcut="⇧⌘Z"
        />
        <Button
          variant="secondary"
          background="transparent"
          icon={IconFeedback}
          size="large"
          tooltip="Feedback"
          tooltipPlacement="horizontal"
          onClick={() => {
            navigation.toggleToplevel('feedback');
          }}
          checked={toplevel === 'feedback'}
          ref={feedbackButtonRef}
        />
      </div>
    </aside>
  );
});

const DesignSyncControls = observer(function _DesignSyncControls() {
  const designStore = getGlobalDesignStore();
  const conceptsStore = getGlobalConceptsStore();

  designStore.ensureDesignInitialized();

  const isActive =
    designStore.design.linkedConcept.state.uuid ===
    conceptsStore.activeConcept.state.uuid;

  return isActive ? (
    <div className={styles.activeIndicator}>
      <div className={styles.activeIndicatorDot} />
      <TextSmall>Used in Design</TextSmall>
    </div>
  ) : null;
});

const globalNavigation = new SidebarNavigation();
